import React, { useState, useEffect } from "react";
import { Dropdown, Modal, Button } from "react-bootstrap";
import Switch from "react-switch";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  appApi,
  useGetVehiclesQuery,
  useGetCompaniesQuery,
  useGetVehicleFuelSummaryQuery,
} from "../../../store/appApi";
import {
  toggleVehicleStatusApi,
  deleteVehicleApi,
  updateVehicleApi,
} from "../../../services/vehicle";
import moment from "moment";
import { selectUser, selectHasModuleAccess } from "../../../store/authSlice";

const AllVehicles = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const hasVehiclesWrite = useSelector(
    selectHasModuleAccess("vehicles", "read-write"),
  );

  // ── List state ────────────────────────────────────────────────────────────
  const [sort, setSort] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc");
  const [iconData, setIconData] = useState({ ind: null, asc: false });
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ── Edit modal state ──────────────────────────────────────────────────────
  const [editModal, setEditModal] = useState({ show: false, vehicle: null });
  const [editForm, setEditForm] = useState({
    vehicleMake: "",
    vehicleNumber: "",
    vehicleModel: "",
    initialKm: "",
    companyId: "",
  });
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  // ── Debounce search ───────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── RTK Query ─────────────────────────────────────────────────────────────
  const {
    data: vehiclesResponse,
    isLoading,
    isFetching,
  } = useGetVehiclesQuery({
    page,
    limit: sort,
    search: debouncedSearch,
    sortBy,
    sortOrder,
  });

  // Fuel summary
  const { data: fuelSummaryData, isLoading: fuelSummaryLoading } =
    useGetVehicleFuelSummaryQuery();
  const fuelSummary = Array.isArray(fuelSummaryData?.summary)
    ? fuelSummaryData.summary
    : [];
  const [showFuelSummary, setShowFuelSummary] = useState(false);

  // Company list for the edit modal dropdown
  const { data: companiesResponse } = useGetCompaniesQuery();
  const companies = Array.isArray(companiesResponse?.data)
    ? companiesResponse.data
    : [];

  const rawList = Array.isArray(vehiclesResponse?.data)
    ? vehiclesResponse.data
    : [];
  const pagination = vehiclesResponse?.pagination || {};
  const total = Number(pagination.total) || 0;
  const totalPages = Number(pagination.totalPages) || 1;
  const showingFrom = total > 0 ? (page - 1) * sort + 1 : 0;
  const showingTo = total > 0 ? Math.min(page * sort, total) : 0;
  const showLoading = isLoading || isFetching;

  // ── Sorting ───────────────────────────────────────────────────────────────
  const sortFieldMap = {
    make: "vehicleMake",
    number: "vehicleNumber",
    created: "created",
  };

  const onSort = (field, ind) => {
    const backendField = sortFieldMap[field];
    if (!backendField) return;
    const nextOrder =
      sortBy === backendField && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(backendField);
    setSortOrder(nextOrder);
    setPage(1);
    setIconData({ ind, asc: nextOrder === "asc" });
  };

  // ── Toggle Status ─────────────────────────────────────────────────────────
  const handleToggleStatus = async (vehicle) => {
    setTogglingId(vehicle._id);
    try {
      await toggleVehicleStatusApi(vehicle._id, !vehicle.status);
      dispatch(appApi.util.invalidateTags(["Vehicles"]));
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update status.",
      });
    } finally {
      setTogglingId(null);
    }
  };

  // ── Delete (super-admin only) ─────────────────────────────────────────────
  const handleDelete = async (vehicle) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Vehicle?",
      text: `This will permanently remove "${vehicle.vehicleMake} — ${vehicle.vehicleNumber}".`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;

    setDeletingId(vehicle._id);
    try {
      await deleteVehicleApi(vehicle._id);
      dispatch(appApi.util.invalidateTags(["Vehicles"]));
      Swal.fire({
        icon: "success",
        title: "Deleted",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to delete vehicle.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setDeletingId(null);
    }
  };

  // ── Edit (super-admin only) ───────────────────────────────────────────────
  const openEditModal = (vehicle) => {
    setEditForm({
      vehicleMake: vehicle.vehicleMake || "",
      vehicleNumber: vehicle.vehicleNumber || "",
      vehicleModel: vehicle.vehicleModel || "",
      initialKm:
        vehicle.initialKm === undefined || vehicle.initialKm === null
          ? ""
          : String(vehicle.initialKm),
      companyId: vehicle.companyId || "",
    });
    setEditErrors({});
    setEditModal({ show: true, vehicle });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.vehicleMake.trim()) e.vehicleMake = "Make is required.";
    if (!editForm.vehicleNumber.trim()) e.vehicleNumber = "Number is required.";
    if (!editForm.vehicleModel.trim()) e.vehicleModel = "Model is required.";
    if (editForm.initialKm !== "" && Number(editForm.initialKm) < 0)
      e.initialKm = "Kilometers cannot be negative.";
    // if (!editForm.companyId) e.companyId = "Company is required.";
    return e;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const errs = validateEdit();
    if (Object.keys(errs).length) {
      setEditErrors(errs);
      return;
    }

    setEditLoading(true);
    try {
      await updateVehicleApi({ _id: editModal.vehicle._id, ...editForm });
      dispatch(appApi.util.invalidateTags(["Vehicles"]));
      setEditModal({ show: false, vehicle: null });
      Swal.fire({
        icon: "success",
        title: "Updated",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update vehicle.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setEditLoading(false);
    }
  };

  const theadData = [
    { heading: "Make", field: "make" },
    { heading: "Number", field: "number" },
    { heading: "Model", field: null },
    { heading: "Company", field: null },
    { heading: "Assigned To", field: null },
    { heading: "Added On", field: "created" },
    { heading: "Status", field: null },
    { heading: "Actions", field: null },
  ];

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <>
      <PageTitle activeMenu="All Vehicles" motherMenu="Vehicles / Machines" />

      {/* ── Fuel / Consumption Summary Card ────────────────────────────────── */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="card">
            <div
              className="card-header d-flex align-items-center gap-2"
              style={{ cursor: "pointer" }}
              onClick={() => setShowFuelSummary((v) => !v)}
            >
              <h5 className="card-title mb-0">
                <i className="fa fa-tint me-2 text-warning" />
                Fuel / Consumption Summary
              </h5>
              {!fuelSummaryLoading && (
                <span className="badge badge-warning">
                  {fuelSummary.length} vehicles
                </span>
              )}
              <i
                className={`fa fa-chevron-${showFuelSummary ? "up" : "down"} ms-auto text-muted`}
              />
            </div>
            {showFuelSummary && (
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="display dataTable no-footer w-100">
                    <thead>
                      <tr>
                        {/* <th>#</th> */}
                        <th>Vehicle</th>
                        <th>Make / Model</th>
                        <th>Total Qty Used</th>
                        <th>Total Cost (₹)</th>
                        <th>Entries</th>
                        <th>Last Entry</th>
                        <th>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fuelSummaryLoading && (
                        <tr>
                          <td colSpan={8} className="text-center py-4">
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading…
                          </td>
                        </tr>
                      )}
                      {!fuelSummaryLoading && fuelSummary.length === 0 && (
                        <tr>
                          <td
                            colSpan={8}
                            className="text-center py-4 text-muted"
                          >
                            No fuel or consumption entries logged yet.
                          </td>
                        </tr>
                      )}
                      {!fuelSummaryLoading &&
                        fuelSummary.map((row, idx) => (
                          <tr key={row._id}>
                            {/* <td className="text-muted small">{idx + 1}</td> */}
                            <td
                              className="fw-bold"
                              style={{ textTransform: "uppercase" }}
                            >
                              {row.vehicleNumber || "—"}
                            </td>
                            <td className="text-muted small">
                              {row.vehicleMake} {row.vehicleModel}
                            </td>
                            <td className="fw-semibold">
                              {Number(row.totalQty).toLocaleString("en-IN")}
                            </td>
                            <td className="fw-semibold">
                              ₹{" "}
                              {Number(row.totalCost).toLocaleString("en-IN", {
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td className="text-muted small">{row.entries}</td>
                            <td className="text-muted small">
                              {row.lastEntry
                                ? new Date(row.lastEntry).toLocaleDateString(
                                    "en-IN",
                                    {
                                      dateStyle: "medium",
                                    },
                                  )
                                : "—"}
                            </td>
                            <td>
                              <Link
                                to={`/vehicle/${row._id}/history`}
                                className="btn btn-xs btn-outline-info"
                                title="View full detail"
                              >
                                <i className="fa fa-eye" />
                              </Link>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">All Vehicles / Machines</h4>
              {hasVehiclesWrite && (
                <Link to="/add-vehicle" className="btn btn-primary btn-sm">
                  + Add Vehicle
                </Link>
              )}
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <div className="dataTables_wrapper no-footer">
                  {/* Controls row */}
                  <div className="d-sm-flex justify-content-between mb-3">
                    <div className="dataTables_length">
                      <label className="d-flex align-items-center gap-2">
                        Show
                        <Dropdown>
                          <Dropdown.Toggle as="div" className="search-drop-btn">
                            {sort}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {[10, 20, 30].map((n) => (
                              <Dropdown.Item
                                key={n}
                                onClick={() => {
                                  setSort(n);
                                  setPage(1);
                                }}
                              >
                                {n}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                        entries
                      </label>
                    </div>
                    <div className="dataTables_filter">
                      <label className="d-flex align-items-center gap-2">
                        Search:
                        <input
                          type="search"
                          className="form-control form-control-sm"
                          placeholder="Make or number…"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </label>
                    </div>
                  </div>

                  <table className="display dataTable no-footer w-100">
                    <thead>
                      <tr>
                        {theadData.map((col, ind) => (
                          <th
                            key={ind}
                            onClick={() => col.field && onSort(col.field, ind)}
                            style={{
                              cursor: col.field ? "pointer" : "default",
                            }}
                          >
                            {col.heading}
                            {col.field && (
                              <span>
                                {iconData.ind !== ind && (
                                  <i
                                    className="fa fa-sort ms-2 fs-12"
                                    style={{ opacity: 0.3 }}
                                  />
                                )}
                                {iconData.ind === ind && (
                                  <i
                                    className={`fa fa-arrow-${iconData.asc ? "up" : "down"} ms-2 fs-12`}
                                    style={{ opacity: 0.7 }}
                                  />
                                )}
                              </span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {showLoading && (
                        <tr>
                          <td colSpan={8} className="text-center py-4">
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading vehicles…
                          </td>
                        </tr>
                      )}
                      {!showLoading && rawList.length === 0 && (
                        <tr>
                          <td
                            colSpan={8}
                            className="text-center py-4 text-muted"
                          >
                            No vehicles found.
                          </td>
                        </tr>
                      )}
                      {rawList.map((v) => (
                        <tr key={v._id}>
                          <td style={{ textTransform: "capitalize" }}>
                            {v.vehicleMake || "—"}
                          </td>
                          <td style={{ textTransform: "uppercase" }}>
                            {v.vehicleNumber || "—"}
                          </td>
                          <td>{v.vehicleModel || "—"}</td>
                          <td>{v.companyName || "—"}</td>
                          <td>
                            {v.assignedTo ? (
                              <Link
                                to={`/vehicle/${v._id}/history`}
                                className="fw-semibold text-primary"
                                title="View assignment history"
                              >
                                <i className="fa fa-user me-1" />
                                {v.assignedTo}
                              </Link>
                            ) : (
                              <span className="text-muted small">
                                Unassigned
                              </span>
                            )}
                          </td>
                          <td>
                            {v.createdAt
                              ? moment(v.createdAt).format("MMMM Do YYYY")
                              : "—"}
                          </td>
                          <td>
                            <Switch
                              onChange={() => handleToggleStatus(v)}
                              checked={v.status === true}
                              disabled={togglingId === v._id}
                              onColor="#4d6542"
                              offColor="#ff5e5e"
                              uncheckedIcon={false}
                              checkedIcon={false}
                              height={22}
                              width={44}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <Link
                                to={`/vehicle/${v._id}/history`}
                                className="btn btn-xs btn-outline-info"
                                title="Assignment History"
                              >
                                <i className="fa fa-history" />
                              </Link>
                              {hasVehiclesWrite && (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-outline-primary"
                                    title="Edit"
                                    onClick={() => openEditModal(v)}
                                  >
                                    <i className="fa fa-pencil" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-outline-danger"
                                    title="Delete"
                                    disabled={deletingId === v._id}
                                    onClick={() => handleDelete(v)}
                                  >
                                    {deletingId === v._id ? (
                                      <span className="spinner-border spinner-border-sm" />
                                    ) : (
                                      <i className="fa fa-trash" />
                                    )}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                    <div className="dataTables_info">
                      Showing {showingFrom} to {showingTo} of {total} entries
                    </div>
                    <div className="dataTables_paginate paging_simple_numbers">
                      <Link
                        className={`paginate_button previous ${page <= 1 ? "disabled" : ""}`}
                        to="#"
                        onClick={() => page > 1 && setPage(page - 1)}
                      >
                        Previous
                      </Link>
                      <span>
                        {pageNumbers.map((n) => (
                          <Link
                            key={n}
                            to="#"
                            className={`paginate_button ${page === n ? "current" : ""}`}
                            onClick={() => setPage(n)}
                          >
                            {n}
                          </Link>
                        ))}
                      </span>
                      <Link
                        className={`paginate_button next ${page >= totalPages ? "disabled" : ""}`}
                        to="#"
                        onClick={() => page < totalPages && setPage(page + 1)}
                      >
                        Next
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit Modal (super-admin only) ─────────────────────────────────── */}
      <Modal
        show={editModal.show}
        onHide={() => setEditModal({ show: false, vehicle: null })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Vehicle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form id="edit-vehicle-form" onSubmit={handleEditSubmit}>
            <div className="form-group mb-3">
              <label className="form-label">
                Vehicle Make <span className="text-danger">*</span>
              </label>
              <input
                name="vehicleMake"
                className={`form-control ${editErrors.vehicleMake ? "is-invalid" : ""}`}
                value={editForm.vehicleMake}
                onChange={handleEditChange}
              />
              {editErrors.vehicleMake && (
                <div className="invalid-feedback">{editErrors.vehicleMake}</div>
              )}
            </div>
            <div className="form-group mb-3">
              <label className="form-label">
                Vehicle Number <span className="text-danger">*</span>
              </label>
              <input
                name="vehicleNumber"
                className={`form-control ${editErrors.vehicleNumber ? "is-invalid" : ""}`}
                value={editForm.vehicleNumber}
                onChange={handleEditChange}
              />
              {editErrors.vehicleNumber && (
                <div className="invalid-feedback">
                  {editErrors.vehicleNumber}
                </div>
              )}
            </div>
            <div className="form-group mb-3">
              <label className="form-label">
                Vehicle Model <span className="text-danger">*</span>
              </label>
              <input
                name="vehicleModel"
                className={`form-control ${editErrors.vehicleModel ? "is-invalid" : ""}`}
                value={editForm.vehicleModel}
                onChange={handleEditChange}
              />
              {editErrors.vehicleModel && (
                <div className="invalid-feedback">
                  {editErrors.vehicleModel}
                </div>
              )}
            </div>
            <div className="form-group mb-3">
              <label className="form-label">
                Initial Kilometers{" "}
                <span className="text-muted small fw-normal">
                  (already run)
                </span>
              </label>
              <div className="input-group">
                <input
                  name="initialKm"
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  className={`form-control ${editErrors.initialKm ? "is-invalid" : ""}`}
                  placeholder="e.g. 45200"
                  value={editForm.initialKm}
                  onChange={handleEditChange}
                />
                <span className="input-group-text">km</span>
              </div>
              {editErrors.initialKm && (
                <div className="invalid-feedback d-block">
                  {editErrors.initialKm}
                </div>
              )}
            </div>
            <div className="form-group mb-3">
              <label className="form-label">
                Company <span className="text-danger">*</span>
              </label>
              <select
                name="companyId"
                className={`form-control ${editErrors.companyId ? "is-invalid" : ""}`}
                value={editForm.companyId}
                onChange={handleEditChange}
              >
                <option value="">Select Company</option>
                {companies.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {editErrors.companyId && (
                <div className="invalid-feedback">{editErrors.companyId}</div>
              )}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEditModal({ show: false, vehicle: null })}
            disabled={editLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="edit-vehicle-form"
            disabled={editLoading}
          >
            {editLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AllVehicles;
